/**
 * Choose objectives and, for each, the KPIs it brings: one list for pulling objectives into a
 * year (CopyObjectivesPanel) and for building a template from last year's (TemplateCreationWizard).
 * Ticking an objective ticks all its KPIs and lists them, each with its target and how often it
 * is measured; at least one stays ticked. "Show KPIs" lists them without choosing the objective.
 */
import { useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { BarChart3, CheckCircle2, ChevronDown, ChevronUp, Users } from 'lucide-react'
import { kpiFrequency, kpiTarget, plural, type KpiSelection, type PickableKpi, type PickableObjective } from './kpiSelection'

interface ObjectiveKpiPickerProps {
  objectives: PickableObjective[]
  selection: KpiSelection
  onChange: (selection: KpiSelection) => void
  disabled?: boolean
  /** Badge text for an objective already added. */
  alreadyAddedLabel?: string
  /** Prefixes element ids, so two pickers on a page don't clash. */
  idPrefix?: string
  ariaLabel?: string
}

function KpiLine({ kpi }: { kpi: PickableKpi }) {
  const target = kpiTarget(kpi)
  const frequency = kpiFrequency(kpi)
  return (
    <span className="flex-1 min-w-0">
      <span className="block text-sm text-gray-800">{kpi.name}</span>
      {(target || frequency) && (
        <span className="block text-xs text-gray-600 mt-0.5">
          {target}
          {target && frequency && ', '}
          {frequency && <>measured {frequency}</>}
        </span>
      )}
      {kpi.description && (
        <span className="block text-xs text-muted-foreground mt-0.5 line-clamp-2">{kpi.description}</span>
      )}
    </span>
  )
}

export default function ObjectiveKpiPicker({
  objectives,
  selection,
  onChange,
  disabled = false,
  alreadyAddedLabel = 'Already added',
  idPrefix = 'pick',
  ariaLabel,
}: ObjectiveKpiPickerProps) {
  const [opened, setOpened] = useState<Set<string>>(new Set())

  const toggleObjective = (objective: PickableObjective) => {
    const next = { ...selection }
    if (next[objective.id]) delete next[objective.id]
    else next[objective.id] = objective.kpis.map(k => k.id)
    onChange(next)
  }

  const toggleKpi = (objective: PickableObjective, kpiId: string) => {
    const chosen = selection[objective.id] ?? []
    if (chosen.includes(kpiId)) {
      if (chosen.length === 1) return // at least one stays
      onChange({ ...selection, [objective.id]: chosen.filter(id => id !== kpiId) })
    } else {
      // keep the objective's own order
      const kept = new Set([...chosen, kpiId])
      onChange({ ...selection, [objective.id]: objective.kpis.map(k => k.id).filter(id => kept.has(id)) })
    }
  }

  const toggleOpened = (id: string) => setOpened(prev => {
    const next = new Set(prev)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    return next
  })

  return (
    <ul className="space-y-2" aria-label={ariaLabel}>
      {objectives.map(objective => {
        const checkboxId = `${idPrefix}-objective-${objective.id}`
        const chosen = selection[objective.id]
        const isChosen = chosen !== undefined
        const count = objective.kpis.length
        const showKpis = count > 0 && (isChosen || opened.has(objective.id))
        return (
          <li
            key={objective.id}
            className={`rounded-lg border p-3 transition-colors ${
              isChosen
                ? 'border-heritage-blue bg-blue-50/50'
                : objective.alreadyAdded ? 'bg-gray-50' : 'hover:bg-gray-50'
            }`}
          >
            <div className="flex items-start gap-3">
              <Checkbox
                id={checkboxId}
                checked={isChosen}
                disabled={objective.alreadyAdded || disabled}
                onCheckedChange={() => toggleObjective(objective)}
                className="mt-1"
              />
              <div className="flex-1 min-w-0">
                <label htmlFor={checkboxId} className="block cursor-pointer">
                  <span className="block font-medium text-sm">{objective.title}</span>
                  {objective.description && (
                    <span className="block text-xs text-muted-foreground mt-0.5 line-clamp-2">{objective.description}</span>
                  )}
                </label>
                <span className="flex flex-wrap items-center gap-2 mt-2">
                  {objective.kpaName && <Badge variant="secondary">{objective.kpaName}</Badge>}
                  <Badge variant="outline" className="gap-1">
                    <BarChart3 className="h-3 w-3" />
                    {isChosen && chosen.length < count ? `${chosen.length} of ${count} KPIs` : plural(count, 'KPI')}
                  </Badge>
                  {objective.heldBy !== undefined && objective.heldBy > 1 && (
                    <Badge variant="outline" className="gap-1">
                      <Users className="h-3 w-3" />
                      Held by {objective.heldBy} team members
                    </Badge>
                  )}
                  {objective.alreadyAdded && (
                    <Badge variant="outline" className="gap-1 text-emerald-700 border-emerald-300">
                      <CheckCircle2 className="h-3 w-3" />
                      {alreadyAddedLabel}
                    </Badge>
                  )}
                  {count > 0 && !isChosen && (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 text-xs font-medium text-heritage-blue hover:underline"
                      aria-expanded={opened.has(objective.id)}
                      onClick={() => toggleOpened(objective.id)}
                    >
                      {opened.has(objective.id) ? 'Hide KPIs' : 'Show KPIs'}
                      {opened.has(objective.id) ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                    </button>
                  )}
                </span>

                {showKpis && (
                  <div className="mt-3 rounded-lg bg-gray-50 p-3">
                    {isChosen && (
                      <p className="text-xs font-semibold text-gray-700 mb-2">
                        KPIs to bring (at least one)
                      </p>
                    )}
                    <ul className="space-y-2" aria-label={`KPIs of ${objective.title}`}>
                      {objective.kpis.map(kpi => {
                        if (!isChosen) {
                          return (
                            <li key={kpi.id} className="flex items-start gap-3 rounded-md border border-gray-200 bg-white p-2.5">
                              <BarChart3 className="h-4 w-4 mt-0.5 text-gray-400 flex-shrink-0" />
                              <KpiLine kpi={kpi} />
                            </li>
                          )
                        }
                        const kpiCheckboxId = `${checkboxId}-kpi-${kpi.id}`
                        const isChecked = chosen.includes(kpi.id)
                        const isLast = isChecked && chosen.length === 1
                        return (
                          <li
                            key={kpi.id}
                            className={`flex items-start gap-3 rounded-md border p-2.5 ${
                              isChecked ? 'bg-purple-50 border-purple-200' : 'bg-white border-gray-200'
                            }`}
                          >
                            <Checkbox
                              id={kpiCheckboxId}
                              checked={isChecked}
                              disabled={isLast || disabled}
                              onCheckedChange={() => toggleKpi(objective, kpi.id)}
                              className="mt-0.5"
                            />
                            <label htmlFor={kpiCheckboxId} className={`flex-1 min-w-0 ${isLast ? '' : 'cursor-pointer'}`}>
                              <KpiLine kpi={kpi} />
                            </label>
                            {isLast && count > 1 && (
                              <span className="text-xs text-amber-600 font-medium whitespace-nowrap">At least one</span>
                            )}
                          </li>
                        )
                      })}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </li>
        )
      })}
    </ul>
  )
}
